import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { formatOnsetDate } from 'src/app/shared/util/icd-chip-state.util';

/**
 * The codes this patient no longer carries (§11).
 *
 * ICD-10 codes are never hard-deleted on this platform — removing one sets `active: false` and
 * `status: 'superseded'`, because codes carry `connectSubmissionStatus`, are referenced by
 * submitted claim lines and can carry PMB entitlement. The model's `defaultScope` then hides them
 * from every read, which is correct everywhere except here: this is the one screen whose whole
 * purpose is the history, so a clinician can see that a patient was coded C71.9 for three months
 * before histology moved them to C71.1, and who made that call.
 *
 * Read-only. There is no restore path, by design — bringing a code back changes a billable
 * clinical record and belongs behind the same review the pencil offers, not behind a history view.
 *
 * Purely presentational: the rows arrive through `MAT_DIALOG_DATA` already loaded by the patient
 * file, which needs the same data to decide whether to show the icon at all. No service call, so
 * no loading state and no second fetch path to keep in step with that one.
 */
@Component({
  selector: 'app-icd-archived-codes-dialog',
  templateUrl: './icd-archived-codes-dialog.component.html',
  styleUrls: ['./icd-archived-codes-dialog.component.scss']
})
export class IcdArchivedCodesDialogComponent {

  patientName: string;
  codes: any[];

  constructor(
    private dialogRef: MatDialogRef<IcdArchivedCodesDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.patientName = data?.patientName || 'this patient';
    this.codes = data?.codes || [];
  }

  /**
   * ⚠ Formatted through the shared helper, NOT the `date` pipe. `onsetDate` is a Sequelize
   * `DATEONLY` arriving as 'YYYY-MM-DD', which `Date` parses as UTC midnight and then renders in
   * local time — showing the last day of the previous month in any timezone behind UTC. The date
   * is a clinical and a billing fact, so being a day out is not cosmetic. `removedAt` IS a real
   * timestamp, which is why the template pipes that one.
   */
  onsetLabel(code: any): string | null {
    return formatOnsetDate(code?.onsetDate);
  }

  /** 'external-cause' → 'external cause'. The stored values are slugs, not labels. */
  roleLabel(code: any): string | null {
    return code?.codeRole ? String(code.codeRole).replace(/-/g, ' ') : null;
  }

  /** How the code got onto the record in the first place. Null where it adds nothing. */
  sourceLabel(code: any): string | null {
    switch (code?.source) {
      case 'ai-auto': return 'AI-applied';
      case 'ai-suggested': return 'AI-suggested';
      case 'import': return 'imported';
      case 'connect': return 'from Connect';
      // 'manual' is the default and the unremarkable case — saying so on every second row would
      // bury the two that are not.
      default: return null;
    }
  }

  /**
   * Who removed it, in words.
   *
   * A name where the trail has one. `actorType` alone — the raw 'user' the suggestions dialog
   * shows — answers nothing on a screen that exists to say who changed the coding.
   */
  removedByLabel(code: any): string | null {
    if (code?.removedByName) return code.removedByName;
    switch (code?.removedActorType) {
      case 'user': return 'a user';
      case 'ai-auto': return 'the AI coding pipeline';
      case 'system-migration': return 'a system migration';
      case 'connect-sync': return 'a Connect sync';
      case 'import': return 'an import';
      default: return null;
    }
  }

  close(): void {
    this.dialogRef.close();
  }
}
